import { TrashSimple } from "@phosphor-icons/react"
import { useState } from "react"
import { toast } from "react-toastify"

import { Button } from "~/components/reusable/Button"
import {
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
  DialogTrigger,
} from "~/components/reusable/Dialog"
import { trackEvent } from "~/hooks/useTrackingService"
import { type Invitation } from "~/server/api/routers/auth0"
import { api } from "~/utils/api"
import { isProduction } from "~/utils/environment"

type Props = {
  invitation: Invitation
  organizationId?: string
}

export const RevokeInvitationDialog = ({ invitation, organizationId }: Props) => {
  const utils = api.useUtils()
  const [open, setOpen] = useState(false)

  // Outside production only Hakio invitations may be revoked.
  const blockedTarget = !isProduction() && !invitation.invitee.email.includes("hakio")

  const revokeInvitation = api.auth0.revokeInvitation.useMutation({
    onError: async () => {
      toast.error("Failed to revoke invitation")
    },
    onSuccess: async () => {
      trackEvent("User Invitation Revoked")
      toast.success("Invitation for " + invitation.invitee.email + " has been revoked")
      void utils.auth0.getInvitations.invalidate()
      setOpen(false)
    },
  })

  return (
    <DialogRoot open={open} onOpenChange={setOpen}>
      <DialogTrigger
        requires="administer"
        disabled={blockedTarget}
        className="cursor-pointer disabled:cursor-not-allowed disabled:text-fg-disabled"
      >
        <TrashSimple />
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Are you absolutely sure?</DialogTitle>
        </DialogHeader>
        <div className="text-sm">
          You are about to revoke the invitation for &quot;{invitation.invitee.email}&quot;. The
          link in the invitation email will no longer work.
        </div>
        <DialogFooter className="flex justify-between">
          <DialogClose>
            <Button asChild variant="transparent">
              Cancel
            </Button>
          </DialogClose>
          <Button
            variant="danger"
            requires="administer"
            loading={revokeInvitation.isPending}
            onClick={() => revokeInvitation.mutate({ invitationId: invitation.id, organizationId })}
          >
            Revoke invitation
          </Button>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  )
}
